/**
 * Month partition key helpers for the AuditLog / AccessLog containers (`pk = yyyyMM`, UTC).
 * The `<input type="month">` value format is `YYYY-MM`.
 */

const MONTH_PK_PATTERN = /^\d{6}$/;
const MONTH_INPUT_PATTERN = /^(\d{4})-(\d{2})$/;

export function buildMonthPk(date: Date = new Date()): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${year}${month}`;
}

export function monthInputToPk(monthInput: string): string | null {
  const match = MONTH_INPUT_PATTERN.exec(String(monthInput ?? "").trim());
  if (!match) {
    return null;
  }
  const month = Number(match[2]);
  if (month < 1 || month > 12) {
    return null;
  }
  return `${match[1]}${match[2]}`;
}

export function pkToMonthInput(monthPk: string): string {
  const value = String(monthPk ?? "").trim();
  if (!MONTH_PK_PATTERN.test(value)) {
    return "";
  }
  return `${value.slice(0, 4)}-${value.slice(4)}`;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Distinct UTC month partition keys (`yyyyMM`) for every local calendar day in `[from, to]`.
 * Uses local noon per day so `buildMonthPk` aligns with how logs are partitioned at write time.
 */
export function monthPksInLocalRange(from: Date, to: Date): string[] {
  let start = startOfLocalDay(from);
  let end = startOfLocalDay(to);
  if (start > end) {
    const swap = start;
    start = end;
    end = swap;
  }

  const set = new Set<string>();
  const cursor = new Date(start);
  while (cursor <= end) {
    set.add(
      buildMonthPk(
        new Date(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate(),
          12,
          0,
          0,
          0,
        ),
      ),
    );
    cursor.setDate(cursor.getDate() + 1);
  }
  return [...set].sort();
}
